import React, { useState, useEffect, useContext } from "react";
import { AccountContext } from "../../store/AccountContext";
import {
  Alert,
  Button,
  Typography,
  Grid,
  Box,
  Checkbox,
  Radio,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { Link } from "react-router-dom";
import usePromise from "react-use-promise";

// Card Icons
import visa from "payment-icons/min/flat/visa.svg";
import mastercard from "payment-icons/min/flat/mastercard.svg";
import amex from "payment-icons/min/flat/amex.svg";
import discover from "payment-icons/min/flat/discover.svg";
import cvcIcon from "./assets/cvc_icon.png";
import CircularProgress from "@mui/material/CircularProgress";

import useLogger from "../../hooks/useLogger";
import { CurrencySymbol } from "@booyaltd/core";
import { v4 as uuidv4 } from "uuid";

/**
 * PM5 T2 (money path, batch "payment") — converted from MUI v4 + `makeStyles`
 * to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * PAYMENTS ESCALATION CLASS — this IS the checkout. It mounts the three Stripe
 * Elements, mints the `eventId` that dedups the Meta Purchase pixel against the
 * server's CAPI event (#659), creates the PaymentMethod, calls
 * `createStandardSubscriptionStripe`, runs the 5-attempt status poller and
 * drives the SCA (`confirmCardPayment`) branch. NONE of that moved: the
 * conversion touches imports, class names and JSX props only — no handler, no
 * condition, no timeout, no poll count, no message string, no `disabled`
 * expression, no `React.memo` wrapper. `ELEMENT_OPTIONS` is Stripe's own
 * iframe-styling API (it is NOT CSS and cannot be reached from this document),
 * so it is left byte-for-byte alone, and each Element mount node keeps its v4
 * box declarations verbatim so the hosted iframe is measured against the same
 * box as before. PM6 rewrites this file as v5-native new work; this port is
 * deliberately mechanical so that diff stays pure Stripe mechanics.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1) — declared in `useStyles` but never
 * referenced from this file's JSX: `paymentContainerHeader`,
 * `cardElementContainer`, `cardInputError`, `responseText`,
 * `cardPaymentContainer`, `defaultPaymentMethodLabel`, `stripeLogo`.
 * Conversely `className={classes.cardContainer}` (on the wrapper `<div>`) and
 * `className={classes.termsContainer}` (on two `<Box>`es) referenced keys that
 * do not exist in this file's `useStyles` object, so they resolved to
 * `className={undefined}` and applied nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — bare DOM elements and third-party components. None of these
 * accepts an `sx` prop, so `styled()` is the port (guide §1.2). `dark` is a
 * style-only prop and is filtered out of the DOM.
 * ---------------------------------------------------------------------- */

const darkOnly = { shouldForwardProp: (prop) => prop !== "dark" };

// was: useStyles().cardElementLabel — a bare <label>.
const CardElementLabel = styled(
  "label",
  darkOnly,
)(({ dark }) => ({
  color: dark ? "#eee" : "#656565",
  fontSize: "1rem",
  fontWeight: 600,
  fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
}));

// was: useStyles().cardLogo — a bare <img>.
const CardLogoImg = styled("img")({
  height: "18px",
  marginLeft: "5px",
});

// was: useStyles().defaultPaymentMethodCardBrand — a bare <img>.
const CardBrandImg = styled("img")({
  width: "50px",
  maxWidth: "50px",
  paddingRight: "9px",
  paddingTop: "6px",
});

// was: useStyles().defaultPaymentMethodExpDate — a bare <span>.
const ExpDateSpan = styled("span")(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  fontWeight: 400,
  fontSize: "0.8rem",
}));

// was: useStyles().inputField, on the Stripe CardNumberElement / CardCvcElement
// mount nodes. The commented-out `borderRadius` is v4's; carried across as a
// comment so the two rules stay diff-able.
const CardNumberField = styled(CardNumberElement)({
  padding: "12px",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  // borderRadius: "5px"
  border: "1px solid #c3c3c3",
});

const CardCvcField = styled(CardCvcElement)({
  padding: "12px",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  // borderRadius: "5px"
  border: "1px solid #c3c3c3",
});

/**
 * was: `className={`${classes.inputField} ${classes.bottomPaymentCreds}`}` on
 * the Stripe CardExpiryElement mount node.
 *
 * Guide §2, "Two `makeStyles` keys on one element": both classes are
 * single-class specificity, so v4's winner was JSS sheet order — the order of
 * the keys in the `useStyles` object — and `bottomPaymentCreds`
 * (`marginBottom: 0`) is declared AFTER `inputField`. So the rendered box has
 * `margin: 4px 0 0 0`, not `4px 0 15px 0`. Reproduced by listing `margin`
 * first and `marginBottom` after it; do not "tidy" that order.
 */
const CardExpiryField = styled(CardExpiryElement)({
  padding: "12px",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  // borderRadius: "5px"
  border: "1px solid #c3c3c3",
  marginBottom: 0,
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ Guide §2, "`<Grid container>` lost its `width: 100%`". v4's
 * `.MuiGrid-container` declared `width: 100%` unconditionally; v9's
 * `generateGridStyles` emits no width rule at all. Restated verbatim on every
 * converted container (a no-op on the block-level ones).
 */
const v4GridContainerWidthSx = { width: "100%" };

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim: omit `spacing` (v9 default
 * 0 ⇒ `gap: 0px`) and give each item the 8px padding itself. This container's
 * own `width` supersedes `v4GridContainerWidthSx`, exactly as in v4.
 */
const v4SpacingContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

/**
 * ♦ Guide §2, "Grid without a container". This component's own root
 * `<Grid item>` is rendered into `Payment/Steps.js`'s Box (itself an ex-orphan
 * Grid item), and several `<Grid item>`s below sit inside a `<form>` or inside
 * another Grid ITEM — none of them has a Grid CONTAINER as an ancestor, and v9
 * publishes `--Grid-parent-columns` only from a container's `> *` rule, so a
 * `size` `calc()` would be invalid. Ported as `Box`es carrying the literal v4
 * declarations. Every orphan here is 12/12 columns, i.e. 100% at every
 * breakpoint. (`.MuiGrid-item`'s `margin: 0` is a div's initial value, so it is
 * not restated — same as `Footer.js`.)
 */
const orphanGridItemFullSx = {
  boxSizing: "border-box",
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

// was: the root `<Grid item xs sm md className={classes.paymentContainer}>` —
// an orphan Grid item (above) merged with `paymentContainer`. In v4 the Grid
// classes came from MUI's own JSS sheet and this file's class from a later one,
// so the file's rules go last; here the two sets are disjoint.
const paymentContainerSx = (theme) => ({
  ...orphanGridItemFullSx,
  marginTop: theme.spacing(2),
  boxShadow: "none",
  textAlign: "left",
});

// was: useStyles().paymentInformation
const paymentInformationSx = { marginBottom: 0 };

// was: useStyles().paymentInformationTitle
const paymentInformationTitleSx = (dark) => (theme) => ({
  color: dark ? "#eee" : theme.palette.background.ffNavy,
  fontSize: "1.2rem",
  lineHeight: "1.6rem",
  fontWeight: 500,
  marginBottom: theme.spacing(0.5),
});

// was: useStyles().bodyText
const bodyTextSx = (dark) => (theme) => ({
  color: dark ? "#d9d9d9" : theme.palette.background.upgradeAlt,
  marginBottom: theme.spacing(1.5),
  fontSize: "0.9rem",
});

// was: useStyles().validationError
const validationErrorSx = (theme) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
});

/**
 * was: useStyles().termsLabel. The literal `@media (min-width:600px)` /
 * `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
 * to the v4 values (600/960), so the emitted queries are identical.
 */
const termsLabelSx = (theme) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  color: theme.palette.background.ffNavyLighter,
  fontSize: "0.8rem",
  lineHeight: "1rem",
  textAlign: "left",
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.8rem",
    lineHeight: "1rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "0.8rem",
    lineHeight: "1rem",
  },
  "& a": {
    color: theme.palette.background.ffNavyLighter,
    fontWeight: 500,
  },
});

// was: useStyles().midPaymentCreds, merged with the Grid item padding above
// (guide §2 — `Grid`'s `sx` has no array form, so the layers merge into one
// object; the two rules share no property, so order is not load-bearing here).
const midPaymentCredsItemSx = (theme) => ({
  padding: "8px",
  marginTop: "4px",
  [theme.breakpoints.up("sm")]: {
    marginTop: 0,
  },
});

// was: useStyles().confirmContainer
const confirmContainerSx = (theme) => ({
  backgroundColor: "#ddd",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1.5),
  },
});

/**
 * was: useStyles().defaultPaymentMethodContainer, merged with the `<Box>`
 * system props it was used alongside. Guide §2, "`makeStyles` `className`
 * alongside `Box` system props": the ex-system-props go FIRST and the
 * ex-`className` rules after them, which is the order v4 resolved them in.
 * Applied as an `sx` ARRAY at the two call sites, because the first use has an
 * extra `alignItems="center"` system prop and the second does not.
 */
const defaultPaymentMethodContainerSx = (dark) => (theme) => ({
  backgroundColor: dark ? "#d9d9d9" : "#f5f5f5",
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: dark ? "#d9d9d9" : "#fff",
  },
});

// was: useStyles().defaultPaymentCardNumber
const defaultPaymentCardNumberSx = (theme) => ({
  textAlign: "left",
  color: theme.palette.background.ffNavy,
  fontWeight: 500,
  fontSize: "1.1rem",
});

// was: useStyles().defaultPaymentMethodText
const defaultPaymentMethodTextSx = (theme) => ({
  textAlign: "left",
  color: theme.palette.background.ffNavy,
});

// was: useStyles().defaultPaymentMethodChange
const defaultPaymentMethodChangeSx = (theme) => ({
  color: theme.palette.background.ffGreen,
  fontWeight: 500,
  textDecoration: "underline",
  "&:hover": {
    cursor: "pointer",
  },
});

/**
 * was: useStyles().paymentButton, plus a v4 default that has to be restored.
 *
 * PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
 * NOT in the v4 rule. v4's plain `<Button>` defaulted to `color="default"`,
 * whose root style was `color: theme.palette.text.primary`. v5+ deleted
 * `default` and made `primary` the default — and this theme's `primary.main`
 * IS the lime that `typography.button` paints the button background with, so an
 * uncompensated port renders an invisible lime-on-lime "Start Membership"
 * button, i.e. an unclickable-looking checkout CTA.
 */
const paymentButtonSx = (theme) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  width: "100%",
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
  color: "text.primary",
});

const CardPaymentForm = (props) => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    createStandardSubscriptionStripe,
    paymentErrorResponse,
    paymentError,
    getMembershipDetails,
  } = useContext(AccountContext);
  const { logMetricEvent } = useLogger();
  const { dark } = props;

  const ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        "::placeholder": {
          color: "#ccc",
        },
      },
      invalid: {
        color: "#b92020",
      },
    },
  };

  // Renewal Date config
  let renewalDate = new Date();

  if (props.Subscription.entitlementPeriodUnit === "Year") {
    renewalDate = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(
      new Date(
        renewalDate.setFullYear(
          renewalDate.getFullYear() + props.Subscription.entitlementPeriodValue,
        ),
      ),
    );
  } else if (props.Subscription.entitlementPeriodUnit === "Month") {
    renewalDate = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(
      new Date(
        renewalDate.setMonth(
          renewalDate.getMonth() + props.Subscription.entitlementPeriodValue,
        ),
      ),
    );
  } else if (props.Subscription.entitlementPeriodUnit === "Day") {
    renewalDate = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(
      new Date(
        renewalDate.setMonth(
          renewalDate.getMonth() + props.Subscription.entitlementPeriodValue,
        ),
      ),
    );
  }

  const [cardInputError, setCardInputError] = useState(null);
  const [submittingButton, setSubmittingButton] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(Date.now());
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);

  const loading = state === "pending";

  // eslint-disable-next-line
  const [paymentStatus, setPaymentStatus] = useState();

  // eslint-disable-next-line
  const reload = () => setReloadTrigger(Date.now());

  const clearErrors = (event) => {
    setTermsChecked(false);
    setCardInputError("");
  };

  // Set selectedPaymentMethod as 'default' if a storedCard exists
  useEffect(() => {
    setSelectedPaymentMethod(
      membershipDetails && membershipDetails.defaultPaymentMethod
        ? "default"
        : "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipDetails]);

  // #659: the eventId minted at submit rides the createPayment request (the
  // server fires the CAPI Purchase) and this browser pixel event — same id,
  // same event name, so Meta dedups the pair.
  const onPaymentSuccess = (payment, eventId) => {
    logMetricEvent(
      {
        type: "purchase",
        payload: {
          userId: payment.memberId,
          paymentId: payment.id,
          id: payment.productId || "",
          memberStatusId: payment.memberStatusId || "",
          name: payment.productId || "",
          cost: parseFloat(payment.cost || "0"),
          currency: payment.currency || "",
          stripeReference: payment.paymentProviderReference || "",
        },
      },
      eventId,
    );
  };

  const { handlePaymentResponse } = props;

  // Stripe (Card) Submission
  const handleStripeSubmit = async (event) => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    // Block native form submission.
    event.preventDefault();

    //Disable Submit button - block double/multiple clicks/taps
    setSubmittingButton(true);

    try {
      const cardElement = elements.getElement(CardNumberElement);

      const payload = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (payload.error) {
        setCardInputError(payload.error.message);
        // setSubmittingButton(false);
        return;
      } else {
        // Set Pending State
        handlePaymentResponse({
          status: "pending",
          title: "We're hitching your trailer on now...",
          subTitle: "Payment processing...",
          description: "Please do not reload or refresh your browser.",
        });

        setCardInputError(null);
        const productId = props.Subscription.id;
        const stripePaymentToken = payload.paymentMethod.id;
        const eventId = uuidv4();

        //eslint-disable-next-line
        const paymentAttempt = await createStandardSubscriptionStripe({
          productId,
          stripePaymentToken,
          eventId,
        });

        if (paymentAttempt.status === "payment_error") {
          handlePaymentResponse({
            status: "failure",
            title: "Payment Failed",
            subTitle: paymentAttempt.message,
            description:
              "We were unable to charge your card for your FarmFLiX Membership. Please try again.",
            tryAgain: true,
          });
        } else {
          statusPoller(eventId);
        }
      }
    } catch (e) {
      console.log("Error", e);
      handlePaymentResponse({
        status: "failure",
        title: "Payment Failed",
        subTitle: "Error" + e,
        description:
          "We were unable to charge your card for your FarmFLiX Membership. Please try again.",
        tryAgain: true,
      });
    }
  };

  const statusPoller = async (eventId) => {
    // Documentation
    // https://stripe.com/docs/billing/subscriptions/fixed-price#manage-payment-authentication

    // Get the Latest Payment
    const latestPayment = async () => {
      const memberDetails = await getMembershipDetails();
      const paymentDetails = memberDetails.payments[0];
      return paymentDetails;
    };

    // Set a limit on the number of number of attempts to poll for success/failure response
    let pollAttempts = 0;

    // Poll The DB for the Stripe Webhook Callback
    const StripeCallback = async () => {
      const LP = await latestPayment(); //call the loop contents

      // If latest payment entry is pending, call it again in 2 seconds
      if (LP.status === "pending") {
        // Increment the Polling Attempts
        pollAttempts++;
        console.log("Payment pending...");
        if (pollAttempts === 5) {
          // Show timeout message
          handlePaymentResponse({
            status: "timeout",
            title: "Unable to Process Payment",
            subTitle:
              "Unfortunately we cannot process your payment at this time.",
            tryAgain: true,
          });
          return;
        }
        setTimeout(StripeCallback, 2000);
      }
      // If the latest payment entry is failure
      else if (LP.status === "failure") {
        const latestInvoice = JSON.parse(LP.paymentProviderRawPayload);

        // Reason is 'Action Required', then the payment requires action
        if (latestInvoice.payment_intent.status === "requires_action") {
          console.log("Action Required...");
          handlePaymentResponse({
            status: "pending",
            title: "We're hitching your trailer on now...",
            subTitle: "Authentication required...",
            description: "Please do not reload or refresh your browser.",
          });

          const clientSecret = latestInvoice.payment_intent.client_secret;
          const paymentMethod = latestInvoice.payment_intent.payment_method;

          // Pass client_ secret and payment_intent to confirmCardPayment (SCA Modal)
          return stripe
            .confirmCardPayment(clientSecret, {
              payment_method: paymentMethod,
            })
            .then((result) => {
              if (result.error) {
                console.log("Payment Auth failure...", result);
                handlePaymentResponse({
                  status: "failure",
                  title: "Authentication Failed",
                  subTitle:
                    "Your authentication has failed for your payment. Please try again or use a different card.",
                  tryAgain: true,
                });
                return;
              } else {
                if (result.paymentIntent.status === "succeeded") {
                  console.log("Payment Auth success...");
                  onPaymentSuccess(LP, eventId);
                  handlePaymentResponse({
                    status: "success",
                    title: "Woo hoo!",
                    subTitle: "Payment Successfully Processed!",
                    description:
                      "Thank you for your payment. Your FarmFLiX Account is now set up.",
                  });
                  return;
                }
              }
            })
            .catch((error) => {
              handlePaymentResponse({
                status: "failure",
                title: "Payment Failed",
                subTitle:
                  "We were unable to charge your card for your FarmFLiX Membership.  Please try again or use a different card.",
                tryAgain: true,
              });
              return;
            });
        }
        handlePaymentResponse({
          status: "failure",
          title: "Payment Failed",
          subTitle:
            "We were unable to charge your card for your FarmFLiX Membership.  Please try again or use a different card.",
          tryAgain: true,
        });
        return;
      }
      // If the latest payment entry is success, show success message
      else if (LP.status === "success") {
        console.log("Payment Success - No Auth Required.");
        onPaymentSuccess(LP, eventId);
        handlePaymentResponse({
          status: "success",
          title: "Woo hoo!",
          subTitle: "Payment Successfully Processed!",
          description:
            "Thank you for your payment. Your FarmFLiX Account is now set up.",
        });
        return;
      }
    };

    await StripeCallback();
  };

  // Stripe Membership Submission
  const handleNewMembership = async () => {
    handlePaymentResponse({
      status: "pending",
      title: "We're hitching your trailer on now...",
      subTitle: "Payment processing...",
      description: "Please do not reload or refresh your browser.",
    });

    const productId = props.Subscription.id;

    // Passing "default" in to use the current payment method on the Stripe Account
    const stripePaymentToken = membershipDetails.defaultPaymentMethod?.id;
    const eventId = uuidv4();

    const paymentAttempt = await createStandardSubscriptionStripe({
      productId,
      stripePaymentToken,
      eventId,
    });

    if (paymentAttempt) {
      setTimeout(() => statusPoller(eventId), 3000);
    }
  };

  const handleTermsChecked = (event) => {
    setTermsChecked(event.target.checked);
    if (termsChecked) {
      setSubmittingButton(false);
    } else {
      setSubmittingButton(true);
    }
    // setSubmittingButton(true);
  };
  const handlePaymentMethodSelection = (event) => {
    // resetBuyNowErrors();
    setTermsChecked(false);
    setSelectedPaymentMethod(event.target.value);
    setSubmittingButton(false);
  };

  return (
    <>
      <Box sx={paymentContainerSx}>
        <div>
          <Grid container sx={v4GridContainerWidthSx}>
            <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={paymentInformationSx}>
              <Typography sx={paymentInformationTitleSx(dark)} variant="body1">
                Payment
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              {!loading && (
                <>
                  {paymentError && (
                    <>
                      {paymentErrorResponse !== "undefined" && (
                        <Alert
                          sx={validationErrorSx}
                          severity="error"
                          variant="filled"
                        >
                          {paymentErrorResponse}
                        </Alert>
                      )}
                    </>
                  )}
                  {/* If user was a previous member */}
                  {membershipDetails &&
                    membershipDetails.defaultPaymentMethod && (
                      <>
                        {/* If user has Stripe account and a saved card  */}
                        {membershipDetails.defaultPaymentMethod.cardBrand &&
                          membershipDetails.defaultPaymentMethod.id && (
                            <>
                              <Typography variant="body1" sx={bodyTextSx(dark)}>
                                Pay securely with your previous card or use a
                                new card.
                              </Typography>

                              <Box
                                sx={[
                                  { display: "flex", alignItems: "center" },
                                  defaultPaymentMethodContainerSx(dark),
                                ]}
                              >
                                <Box>
                                  {/* ♦ v9 removed `inputProps` from SwitchBase
                                      (Radio/Checkbox/Switch) — it now falls
                                      through into `...other` and is spread onto
                                      the ROOT span, silently dropping the
                                      aria-label. `slotProps.input` reaches the
                                      same <input> v4's `inputProps` did. */}
                                  <Radio
                                    color="primary"
                                    checked={
                                      membershipDetails.defaultPaymentMethod
                                        .cardBrand &&
                                      membershipDetails.defaultPaymentMethod
                                        .id &&
                                      selectedPaymentMethod === "default"
                                    }
                                    onChange={handlePaymentMethodSelection}
                                    value="default"
                                    name="radio-button-demo"
                                    slotProps={{
                                      input: { "aria-label": "Default" },
                                    }}
                                  />
                                </Box>
                                <Box sx={{ flex: "1", alignItems: "center" }}>
                                  <Typography
                                    variant="body1"
                                    sx={defaultPaymentCardNumberSx}
                                  >
                                    **** **** ****{" "}
                                    {
                                      membershipDetails.defaultPaymentMethod
                                        .last4
                                    }
                                    <br />
                                    <ExpDateSpan>
                                      Exp:{" "}
                                      {
                                        membershipDetails.defaultPaymentMethod
                                          .exp.month
                                      }{" "}
                                      /{" "}
                                      {
                                        membershipDetails.defaultPaymentMethod
                                          .exp.year
                                      }
                                    </ExpDateSpan>
                                  </Typography>
                                </Box>
                                <Box sx={{ alignItems: "center" }}>
                                  <Typography
                                    variant="body1"
                                    sx={defaultPaymentMethodChangeSx}
                                  >
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "visa" && (
                                      <CardBrandImg alt="Visa" src={visa} />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "mastercard" && (
                                      <CardBrandImg
                                        alt="Mastercard"
                                        src={mastercard}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "amex" && (
                                      <CardBrandImg
                                        alt="American Express"
                                        src={amex}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "discover" && (
                                      <CardBrandImg
                                        alt="Discover"
                                        src={discover}
                                      />
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={[
                                  { display: "flex" },
                                  defaultPaymentMethodContainerSx(dark),
                                ]}
                              >
                                <Box sx={{ alignItems: "center" }}>
                                  <Radio
                                    color="primary"
                                    checked={selectedPaymentMethod === "new"}
                                    onChange={handlePaymentMethodSelection}
                                    value="new"
                                    name="radio-button-demo"
                                    slotProps={{
                                      input: { "aria-label": "New" },
                                    }}
                                  />
                                </Box>
                                <Box sx={{ alignItems: "center" }}>
                                  <Typography
                                    variant="body1"
                                    sx={defaultPaymentMethodTextSx}
                                    style={{ padding: "10px 0" }}
                                  >
                                    Use a new card
                                  </Typography>
                                </Box>
                              </Box>

                              {selectedPaymentMethod === "default" && (
                                <>
                                  <Box
                                    sx={[
                                      orphanGridItemFullSx,
                                      confirmContainerSx,
                                    ]}
                                  >
                                    <Box sx={{ display: "flex" }}>
                                      <Box>
                                        <Checkbox
                                          color="primary"
                                          style={{ color: "#99ca3c" }}
                                          onChange={handleTermsChecked}
                                          slotProps={{
                                            input: {
                                              name: "terms",
                                              "aria-label": "primary checkbox",
                                            },
                                          }}
                                        />
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          sx={termsLabelSx}
                                        >
                                          I agree to FarmFLiX&apos;s{" "}
                                          <Link to="/terms">
                                            Terms and Conditions
                                          </Link>{" "}
                                          and{" "}
                                          <Link to="/privacy-policy">
                                            Privacy Policy
                                          </Link>
                                          .
                                          {props.Subscription.isSubscription ? (
                                            <>
                                              Your next membership payment will
                                              be taken on <b>{renewalDate}</b>.
                                              FarmFLiX will automatically track
                                              and charge your membership fee{" "}
                                              <b>
                                                (
                                                {CurrencySymbol({
                                                  currency:
                                                    props.Subscription.currency,
                                                })}
                                                {props.Subscription.cost}/
                                                {
                                                  props.Subscription
                                                    .entitlementPeriodUnit
                                                }
                                                )
                                              </b>{" "}
                                              until you cancel your membership.
                                            </>
                                          ) : (
                                            <>
                                              {" "}
                                              This is a one time payment for{" "}
                                              <b>
                                                {
                                                  props.Subscription
                                                    .entitlementPeriodValue
                                                }{" "}
                                                {
                                                  props.Subscription
                                                    .entitlementPeriodUnit
                                                }
                                                &apos;s
                                              </b>{" "}
                                              access to FarmFLiX. This
                                              Membership will automatically
                                              expire in{" "}
                                              {
                                                props.Subscription
                                                  .entitlementPeriodValue
                                              }{" "}
                                              {
                                                props.Subscription
                                                  .entitlementPeriodUnit
                                              }{" "}
                                              and no further charges will be
                                              applied to your account.
                                            </>
                                          )}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={orphanGridItemFullSx}
                                      align="center"
                                    >
                                      <Button
                                        name="submit"
                                        size="large"
                                        onClick={handleNewMembership}
                                        sx={paymentButtonSx}
                                        disabled={
                                          !stripe ||
                                          !props.Subscription.id ||
                                          !submittingButton
                                        }
                                      >
                                        Start Membership
                                      </Button>
                                    </Box>
                                  </Box>
                                </>
                              )}
                            </>
                          )}
                      </>
                    )}
                  {/* Payment Form */}
                  {selectedPaymentMethod !== "default" && (
                    <form onSubmit={handleStripeSubmit}>
                      <Box sx={orphanGridItemFullSx}>
                        <Typography variant="body1" sx={bodyTextSx(dark)}>
                          Please enter your card details below.
                        </Typography>
                        {cardInputError && (
                          <>
                            {cardInputError !== "undefined" && (
                              <Alert
                                sx={validationErrorSx}
                                severity="error"
                                variant="filled"
                              >
                                {cardInputError}
                              </Alert>
                            )}
                          </>
                        )}
                      </Box>
                      <Box sx={orphanGridItemFullSx}>
                        <Box sx={{ display: "flex" }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <CardElementLabel dark={dark} htmlFor="cardNumber">
                              Card Number
                            </CardElementLabel>
                          </Box>
                          <Box>
                            <CardLogoImg src={visa} alt="Visa" />
                            <CardLogoImg src={mastercard} alt="Mastercard" />
                            <CardLogoImg src={amex} alt="American Express" />
                            <CardLogoImg src={discover} alt="Discover" />
                          </Box>
                        </Box>
                        <CardNumberField
                          id="cardNumber"
                          onFocus={clearErrors}
                          options={ELEMENT_OPTIONS}
                        />
                      </Box>
                      <Grid container sx={v4SpacingContainerSx}>
                        <Grid
                          size={{ xs: 12, sm: 6, md: 6 }}
                          sx={midPaymentCredsItemSx}
                        >
                          <CardElementLabel dark={dark} htmlFor="expiry">
                            Card Expiration
                          </CardElementLabel>
                          <CardExpiryField
                            id="expiry"
                            onFocus={clearErrors}
                            options={ELEMENT_OPTIONS}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={v4GridItemSx}>
                          <Box sx={{ display: "flex" }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <CardElementLabel dark={dark} htmlFor="cvc">
                                CVC
                              </CardElementLabel>
                            </Box>
                            <Box>
                              <CardLogoImg src={cvcIcon} alt="CVC" />
                            </Box>
                          </Box>
                          <CardCvcField
                            id="cvc"
                            onFocus={clearErrors}
                            options={ELEMENT_OPTIONS}
                          />
                        </Grid>
                      </Grid>
                      <Box sx={[orphanGridItemFullSx, confirmContainerSx]}>
                        <Box sx={{ display: "flex" }}>
                          <Box>
                            <Checkbox
                              color="primary"
                              style={{ color: "#99ca3c" }}
                              onChange={handleTermsChecked}
                              slotProps={{
                                input: {
                                  name: "terms",
                                  "aria-label": "primary checkbox",
                                },
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={termsLabelSx}>
                              I agree to FarmFLiX&apos;s{" "}
                              <Link to="/terms">Terms and Conditions</Link> and{" "}
                              <Link to="/privacy-policy">Privacy Policy</Link>.
                              {props.Subscription.isSubscription ? (
                                <>
                                  Your next membership payment will be taken on{" "}
                                  <b>{renewalDate}</b>. FarmFLiX will
                                  automatically track and charge your membership
                                  fee{" "}
                                  <b>
                                    (
                                    {CurrencySymbol({
                                      currency: props.Subscription.currency,
                                    })}
                                    {props.Subscription.cost}/
                                    {props.Subscription.entitlementPeriodUnit})
                                  </b>{" "}
                                  until you cancel your membership.
                                </>
                              ) : (
                                <>
                                  {" "}
                                  This is a one time payment for{" "}
                                  <b>
                                    {props.Subscription.entitlementPeriodValue}{" "}
                                    {props.Subscription.entitlementPeriodUnit}
                                    &apos;s
                                  </b>{" "}
                                  access to FarmFLiX. This Membership will
                                  automatically expire in{" "}
                                  {
                                    props.Subscription.entitlementPeriodValue
                                  }{" "}
                                  {props.Subscription.entitlementPeriodUnit} and
                                  no further charges will be applied to your
                                  account.
                                </>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={orphanGridItemFullSx} align="center">
                          <Button
                            name="submit"
                            type="submit"
                            size="large"
                            sx={paymentButtonSx}
                            disabled={
                              !stripe ||
                              !props.Subscription.id ||
                              !submittingButton
                            }
                          >
                            Start Membership
                          </Button>
                        </Box>
                      </Box>
                    </form>
                  )}
                </>
              )}
              {loading && (
                <>
                  <Box sx={{ m: 5 }} align="center">
                    <CircularProgress variant="indeterminate"></CircularProgress>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </div>
      </Box>
    </>
  );
};

export default React.memo(CardPaymentForm);
